import { useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import './AddAssetLoadModal.scss';
import { CustomButton, Error, Loading } from '../../../_common';
import {
    AssetInfoTypeToCreate,
    AssetLoadType,
    CreateAssetLoadInput,
    GetAssestLoadsByLoadId,
    GetAssetInfoByLoadId,
    AssetInfoType as AssetInfoTypeToSave,
    AssetInfoType,
    GetEquipmentsInfo,
    GetEquipmentsInfo_getEquipmentsInfo as EquipmentsInfo,
} from '../../../../types/schemaTypes';
import AddEquipmentsDetails from './AddEquipmentsDetails';
import { Modal } from 'react-bootstrap';
import { GET_ASSET_INFO, GET_ASSET_LOADS, GET_EQUIPMENTS_INFO } from '../../../../graphql/client/queries';
import AddLoadDriverDetails from './AddLoadDriverDetails';
import { DropdownOption } from '../../../_common/SearchableDropdown/SearchableDropdown';

interface AssetInfoToSaveProps {
    index: number;
    assetTypeId: number | null;
    assetType: string | null;
    assetId: number | null;
    assetDescrip: string | null;
    quantity: number | null;
}

interface AssetLoadToSaveType {
    id: number | null;
    assetLoadType: string | null;
    assetLoadTypeId: number | null;
    originType: string | null;
    originJob: string | null;
    originJobId: number | null;
    originLofa: string | null;
    originLofaId: number | null;
    originYard: string | null;
    originYardId: number | null;
    originLongitude: number | null;
    originLatitude: number | null;
    originRegionDescrip: string | null;
    originRegionId: number | null;
    originDistrictDescrip: string | null;
    originDistrictId: number | null;
    destinationType: string | null;
    destinationJob: string | null;
    destinationJobId: number | null;
    destinationLofa: string | null;
    destinationLofaId: number | null;
    destinationYard: string | null;
    destinationYardId: number | null;
    destinationLongitude: number | null;
    destinationLatitude: number | null;
    destinationRegionDescrip: string | null;
    destinationRegionId: number | null;
    destinationDistrictDescrip: string | null;
    destinationDistrictId: number | null;
    trailerType: string | null;
    trailerTypeId: number | null;
    trailer: string | null;
    trailerId: number | null;
    truckNumber: string | null;
    driverName: string | null;
    driverId: number | null;
    status: string | null;
    miles: number | null;
    accessorialCost: number | null;
    payCode: string | null;
    notes: string | null;
    loadCount: number | null;
}

interface TerminalDetailsProps {
    type: string;
    id: number;
    name: string;
}

interface AddAssetLoadModalProps {
    loadType: "Mob" | "Demob" | "New" | "Edit";
    origin?: TerminalDetailsProps;
    destination?: TerminalDetailsProps;
    loadId?: number;
    showModal: boolean;
    loading: boolean;
    error: ErrorLike | undefined;
    onClickCancel: () => void;
    onClickSaveForAdd?: (valuesToCreateLoad: CreateAssetLoadInput) => void;
    onClickSaveForEdit?: (currentValues, newValues) => void;
}

const AddAssetLoadModal = ({
    loadType,
    origin,
    destination,
    loadId,
    showModal,
    loading,
    error,
    onClickCancel,
    onClickSaveForAdd,
    onClickSaveForEdit,
}: AddAssetLoadModalProps): React.ReactElement => {
    const [assetInfoToSave, setAssetInfoToSave] = useState<AssetInfoToSaveProps[]>();
    const [assetLoadToSave, setAssetLoadToSave] = useState<AssetLoadToSaveType>({
        id: 0,
        assetLoadType: (loadType === 'Mob' || (loadType === 'Demob' && destination?.type === "Job") || loadType === 'New') ? 'Mobilization Load' : ((loadType === 'Demob' && ['Lofa', 'Yard'].includes(destination?.type ?? '') ? 'Demobilization Load' : null)),
        assetLoadTypeId: (loadType === 'Mob' || (loadType === 'Demob' && destination?.type === "Job") || loadType === 'New') ? 1 : ((loadType === 'Demob' && ['Lofa', 'Yard'].includes(destination?.type ?? '') ? 2 : null)),
        originType: origin?.type ?? null,
        originJob: origin?.type === "Job" ? origin.name : null,
        originJobId: origin?.type === "Job" ? origin.id : null,
        originLofa: origin?.type === "Lofa" ? origin.name : null,
        originLofaId: origin?.type === "Lofa" ? origin.id : null,
        originYard: origin?.type === "Yard" ? origin.name : null,
        originYardId: origin?.type === "Yard" ? origin.id : null,
        originLongitude: null,
        originLatitude: null,
        originRegionDescrip: null,
        originRegionId: null,
        originDistrictDescrip: null,
        originDistrictId: null,
        destinationType: destination?.type ?? null,
        destinationJob: destination?.type === "Job" ? destination.name : null,
        destinationJobId: destination?.type === "Job" ? destination.id : null,
        destinationLofa: destination?.type === "Lofa" ? destination.name : null,
        destinationLofaId: destination?.type === "Lofa" ? destination.id : null,
        destinationYard: destination?.type === "Yard" ? destination.name : null,
        destinationYardId: destination?.type === "Yard" ? destination.id : null,
        destinationLongitude: null,
        destinationLatitude: null,
        destinationRegionDescrip: null,
        destinationRegionId: null,
        destinationDistrictDescrip: null,
        destinationDistrictId: null,
        trailerType: null,
        trailerTypeId: null,
        trailer: null,
        trailerId: null,
        truckNumber: null,
        driverName: null,
        driverId: null,
        status: "Created",
        miles: null,
        accessorialCost: null,
        payCode: null,
        notes: null,
        loadCount: 1,
    });

    const [assetInfoExists, setAssetInfoExists] = useState(false);
    const [isContinueMode, setIsContinueMode] = useState(false);
    const [validationError, setValidationError] = useState('');

    const isMultiLoadCreate = loadType !== 'Edit' && (assetLoadToSave.loadCount ?? 1) > 1;

    useEffect(() => {
        if (loadType === 'Edit') return;
        if ((assetLoadToSave.loadCount ?? 1) <= 1) return;

        setAssetLoadToSave((prev) => ({
            ...prev,
            driverId: null,
            driverName: null,
            truckNumber: null,
            trailerId: null,
            trailer: null,
        }));

        setAssetInfoToSave((prev) => {
            if (!prev?.length) return prev;
            return prev.map((row) => ({
                ...row,
                assetId: null,
                assetDescrip: '',
            }));
        });
    }, [assetLoadToSave.loadCount, loadType, assetInfoToSave?.length]);

    const assetInfo: AssetInfoType[] = [];
    const { data: assetInfoData, loading: assetInfoLoading, error: assetInfoError } = useQuery<GetAssetInfoByLoadId>(GET_ASSET_INFO, {
        variables: {
            loadId: loadId,
        },
        fetchPolicy: 'network-only',
        skip: loadType !== "Edit",
    });

    useEffect(() => {
        if (loadType !== "Edit" || !assetInfoData?.getAssetInfoByLoadId) return;
        const tempInfoToSave: AssetInfoToSaveProps[] = [];
        (assetInfoData.getAssetInfoByLoadId ?? []).forEach((loadInfo, index) => {
            if (loadInfo != null) tempInfoToSave.push({
                index,
                assetTypeId: loadInfo.assetTypeId,
                assetType: loadInfo.assetType,
                assetId: loadInfo.assetId,
                assetDescrip: loadInfo.assetDescrip,
                quantity: loadInfo.quantity,
            });
        });
        setAssetInfoToSave(tempInfoToSave);
    }, [loadType, assetInfoData]);
    if (loadType === "Edit" && assetInfoData && assetInfoData.getAssetInfoByLoadId) {
        assetInfoData.getAssetInfoByLoadId.map((loadInfo, index) => {
            if (loadInfo) assetInfo.push({
                assetTypeId: loadInfo.assetTypeId,
                assetType: loadInfo.assetType,
                assetId: loadInfo.assetId,
                assetDescrip: loadInfo.assetDescrip,
                quantity: loadInfo.quantity,
            })
        })
    }

    let assetLoad: AssetLoadType | undefined;
    const { data: assetLoadData, loading: assetLoadLoading, error: assetLoadError } = useQuery<GetAssestLoadsByLoadId>(GET_ASSET_LOADS, {
        variables: {
            loadId: loadId,
        },
        fetchPolicy: 'network-only',
        skip: loadType !== "Edit",
    });

    useEffect(() => {
        if (loadType !== "Edit" || !assetLoadData?.getAssestLoadsByLoadId) return;
        const al = assetLoadData.getAssestLoadsByLoadId;
        setAssetLoadToSave({
            id: al.id,
            assetLoadType: al.assetLoadType,
            assetLoadTypeId: al.assetLoadTypeId,
            originType: al.originType,
            originJob: al.originJob,
            originJobId: al.originJobId,
            originLofa: al.originLofa,
            originLofaId: al.originLofaId,
            originYard: al.originYard,
            originYardId: al.originYardId,
            originLongitude: al.originLongitude,
            originLatitude: al.originLatitude,
            originRegionDescrip: al.originRegion,
            originRegionId: al.originRegionId,
            originDistrictDescrip: al.originDistrict,
            originDistrictId: al.originDistrictId,
            destinationType: al.destinationType,
            destinationJob: al.destinationJob,
            destinationJobId: al.destinationJobId,
            destinationLofa: al.destinationLofa,
            destinationLofaId: al.destinationLofaId,
            destinationYard: al.destinationYard,
            destinationYardId: al.destinationYardId,
            destinationLongitude: al.destinationLongitude,
            destinationLatitude: al.destinationLatitude,
            destinationRegionDescrip: al.destinationRegion,
            destinationRegionId: al.destinationRegionId,
            destinationDistrictDescrip: al.destinationDistrict,
            destinationDistrictId: al.destinationDistrictId,
            trailerType: al.trailerType,
            trailerTypeId: al.trailerTypeId,
            trailer: al.trailer,
            trailerId: al.trailerId,
            truckNumber: al.truckNumber,
            driverName: al.driverName,
            driverId: al.driverId,
            status: al.status,
            miles: al.miles,
            accessorialCost: al.accessorialCost,
            payCode: al.payCode,
            notes: null,
            loadCount: 1,
        });
    }, [loadType, assetLoadData]);
    if (loadType === "Edit" && assetLoadData && assetLoadData.getAssestLoadsByLoadId) {
        assetLoad = assetLoadData.getAssestLoadsByLoadId;
    }

    let equipmentsInfo: EquipmentsInfo | undefined;
    // GET_EQUIPMENTS_INFO - New fields to be added in API response
    const { data: equipmentInfoData, loading: equipmentInfoDataLoading, error: equipmentInfoDataError } = useQuery<GetEquipmentsInfo>(GET_EQUIPMENTS_INFO, {
        variables: {
            input: {
                originType: assetLoadToSave.originType,
                originId: assetLoadToSave.originType === "Job" ? assetLoadToSave.originJobId : (assetLoadToSave.originType === "Lofa" ? assetLoadToSave.originLofaId : (assetLoadToSave.originType === "Yard" ? assetLoadToSave.originYardId : (assetLoadToSave.originType === "Other" ? 0 : 0)))
            }
        },
        fetchPolicy: "network-only",
        skip: !assetLoadToSave.originType || (assetLoadToSave.originType !== "Other" && !assetLoadToSave.originJobId && !assetLoadToSave.originLofaId && !assetLoadToSave.originYardId),
    });
    if (equipmentInfoData && equipmentInfoData.getEquipmentsInfo) {
        equipmentsInfo = equipmentInfoData.getEquipmentsInfo;
    }

    const payCodeList: DropdownOption[] = [
        { id: 0, name: "" },
        { id: 1, name: "Forklift (Heavy Haul)" },
        { id: 2, name: "Single Empty Box" },
        { id: 3, name: "Two Empty Boxes" },
        { id: 4, name: "Loaded Box" },
        { id: 5, name: "Cradle" },
        { id: 6, name: "Stepdeck" },
        { id: 7, name: "Loader (Heavy Haul)" },
    ];

    const updatePaycode = () => {
        const tempAssetLoadToSave = { ...assetLoadToSave };

        // If trailer type is 'Stepdeck' or 'Sandbox Chassis' then paycode is defaulted
        if (['Stepdeck', 'Sandbox Chassis', 'Cradle'].includes(tempAssetLoadToSave.trailerType ?? '')) return;

        let payCodeId = -1;
        assetInfoToSave?.map(info => {
            if (info.assetType === "Forklift") payCodeId = 1;
            if (info.assetType === "Front end loader") payCodeId = 7;
            if (info.assetType === "Cradle") payCodeId = 5;
            if (info.assetType === "Sandbox") (payCodeId === 2 || payCodeId === 3) ? payCodeId = 3 : payCodeId = 2;
        })

        if (payCodeId === -1) tempAssetLoadToSave.payCode = payCodeList[6].name.toString();
        else tempAssetLoadToSave.payCode = payCodeList[payCodeId].name.toString();

        // If Trailer is 'Stepdeck' then Paycode must be 'Stepdeck'
        if (assetLoadToSave.trailerType === 'Stepdeck') tempAssetLoadToSave.payCode = payCodeList[6].name.toString();
        // If Trailer is 'Chassis' then Paycode must be 'Single Empty Box'
        if (assetLoadToSave.trailerType === 'Sandbox Chassis') tempAssetLoadToSave.payCode = payCodeList[2].name.toString();
        setAssetLoadToSave(tempAssetLoadToSave);
    };

    const isSaveButtonDisabled = () => {
        let isDisabled = false;
        // Atleast one equipment must be added
        if (loadType !== 'Edit' && (!assetInfoToSave || assetInfoToSave.length === 0)) {
            isDisabled = true;
            return isDisabled;
        }
        const multiLoad = loadType !== 'Edit' && (assetLoadToSave.loadCount ?? 1) > 1;
        assetInfoToSave?.map(assetInfo => {
            // Equipment Type must be selected
            if (!assetInfo.assetType) isDisabled = true;
            // If Assigning Driver -- Equipment ID/ Quantity must be added (not when creating multiple loads)
            if (
                !multiLoad &&
                assetInfo.assetType &&
                assetLoadToSave?.driverName &&
                (
                    (!["Mats", "Forklift Ramp", "Miscellaneous"].includes(assetInfo.assetType) && !assetInfo.assetId) ||
                    (["Mats", "Forklift Ramp", "Miscellaneous"].includes(assetInfo.assetType) && !assetInfo.quantity)
                )
            )
                isDisabled = true;
        })

        // Load type must be selected
        if (!assetLoadToSave.assetLoadTypeId) isDisabled = true;

        // If Assigning driver -- Trailer Type is mandatory
        if (assetLoadToSave?.driverName && !assetLoadToSave?.trailerTypeId) isDisabled = true;
        // If Assigning driver -- Trailer ID is mandatory
        if (assetLoadToSave?.driverName && !assetLoadToSave?.trailerId) isDisabled = true;

        // If stepdeck -- Miles is Mandatory
        if (assetLoadToSave?.trailerTypeId === 6 && !assetLoadToSave.miles) isDisabled = true;
        if (loadType === "New" && !assetLoadToSave.assetLoadType) isDisabled = true;
        
        // Origin is Mandatory
        if (assetLoadToSave?.originType === "Job" && !assetLoadToSave.originJobId) isDisabled = true;
        if (assetLoadToSave?.originType === "Lofa" && !assetLoadToSave.originLofaId) isDisabled = true;
        if (assetLoadToSave?.originType === "Yard" && !assetLoadToSave.originYardId) isDisabled = true;
       
        // Destination is Mandatory
        if (assetLoadToSave?.destinationType === "Job" && !assetLoadToSave.destinationJobId) isDisabled = true;
        if (assetLoadToSave?.destinationType === "Lofa" && !assetLoadToSave.destinationLofaId) isDisabled = true;
        if (assetLoadToSave?.destinationType === "Yard" && !assetLoadToSave.destinationYardId) isDisabled = true;

        // If Latitude is entered then Longitude is Mandatory
        // If Longitude is entered then Latitude is Mandatory
        if (
            assetLoadToSave?.originType === "Other" && (
                (assetLoadToSave.originLatitude && !assetLoadToSave.originLongitude) ||
                (assetLoadToSave.originLongitude && !assetLoadToSave.originLatitude)
            )
        ) isDisabled = true;

        // If Destination type is Other then Latitude and Longitude are Mandatory
        if (
            assetLoadToSave?.destinationType === "Other" && (
                !assetLoadToSave.destinationLongitude || !assetLoadToSave.destinationLatitude
            )
        ) isDisabled = true;

        // Origin Region and District are Mandatory
        if (!assetLoadToSave.originRegionId || !assetLoadToSave.originDistrictId) isDisabled = true;
        // Destination Region and District is Mandatory
        if (!assetLoadToSave.destinationRegionId || !assetLoadToSave.destinationDistrictId) isDisabled = true;

        // Origin and Destination must be different
        if (assetLoadToSave?.originJobId && assetLoadToSave?.originJobId === assetLoadToSave?.destinationJobId) isDisabled = true;
        if (assetLoadToSave?.originLofaId && assetLoadToSave?.originLofaId === assetLoadToSave?.destinationLofaId) isDisabled = true;
        if (assetLoadToSave?.originYardId && assetLoadToSave?.originYardId === assetLoadToSave?.destinationYardId) isDisabled = true;

        // If load type is 'Transfer' then Origin region and Destination region must be different
        if (assetLoadToSave.assetLoadType === "Equipment Transfer" && assetLoadToSave.originRegionId === assetLoadToSave.destinationRegionId) isDisabled = true;
        // If load type is 'Move' then Origin region and Destination region must be the same
        if (assetLoadToSave.assetLoadType === "Equipment Move" && assetLoadToSave.originRegionId !== assetLoadToSave.destinationRegionId) isDisabled = true;

        // Check if asset info exists
        let infoExists = false;
        assetInfoToSave?.map(assetInfo => {
            if (
                !multiLoad &&
                !["Mats", "Forklift Ramp", "Miscellaneous"].includes(assetInfo.assetType ?? '') &&
                assetInfo.assetId
            )
                infoExists = true;
        })
        if (assetInfoExists !== infoExists) setAssetInfoExists(infoExists);

        // Trailer type must be selected if status is not created or rfd
        if (!["Created", "ReadyForDispatch"].includes(assetLoadToSave.status ?? '') && !assetLoadToSave.trailerTypeId) isDisabled = true;

        return isDisabled;
    }

    const handleOnClickSaveForAdd = () => {
        const assetDetails: AssetInfoTypeToCreate[] = [];
        assetInfoToSave?.map(info => {
            if (info.assetTypeId)
                assetDetails.push({
                    assetTypeId: info.assetTypeId,
                    assetId: info.assetId,
                    quantity: info.quantity,
                    assetInfo: info.assetDescrip,
                })
        })
        const valuesToCreateLoad: CreateAssetLoadInput = {
            assetLoadTypeId: assetLoadToSave?.assetLoadTypeId,
            originType: assetLoadToSave?.originType,
            originJobId: assetLoadToSave?.originJobId,
            originLofaId: assetLoadToSave?.originLofaId,
            originYardId: assetLoadToSave?.originYardId,
            originLatitude: assetLoadToSave?.originLatitude,
            originLongitude: assetLoadToSave?.originLongitude,
            originRegionId: assetLoadToSave?.originRegionId,
            originDistrictId: assetLoadToSave?.originDistrictId,
            destinationType: assetLoadToSave?.destinationType,
            destinationJobId: assetLoadToSave?.destinationJobId,
            destinationLofaId: assetLoadToSave?.destinationLofaId,
            destinationYardId: assetLoadToSave?.destinationYardId,
            destinationRegionId: assetLoadToSave?.destinationRegionId,
            destinationDistrictId: assetLoadToSave?.destinationDistrictId,
            destinationLatitude: assetLoadToSave?.destinationLatitude,
            destinationLongitude: assetLoadToSave?.destinationLongitude,
            driverId: assetLoadToSave?.driverId,
            trailerTypeId: assetLoadToSave?.trailerTypeId,
            trailerId: assetLoadToSave?.trailerId,
            miles: assetLoadToSave?.miles,
            truckNumber: assetLoadToSave?.truckNumber,
            accessorialCost: assetLoadToSave?.accessorialCost,
            payCode: assetLoadToSave.payCode,
            assetDetails: assetDetails,
            notes: assetLoadToSave.notes ?? null,
            loadCount: assetLoadToSave.loadCount ?? 1,
        }

        onClickSaveForAdd && onClickSaveForAdd(valuesToCreateLoad);
    }

    const handleOnClickSaveForEdit = () => {
        const currentDetails: AssetInfoTypeToSave[] = [];
        assetInfo.map(info => {
            currentDetails.push({
                assetTypeId: info.assetTypeId,
                assetType: info.assetType,
                quantity: info.quantity,
                assetId: info.assetId,
                assetDescrip: info.assetDescrip,
            })
        })
        const currentValues: AssetLoadType = {
            assetLoadTypeId: assetLoad?.assetLoadTypeId,
            originType: assetLoad?.originType,
            originJobId: assetLoad?.originJobId,
            originLofaId: assetLoad?.originLofaId,
            originYardId: assetLoad?.originYardId,
            originLatitude: assetLoad?.originLatitude,
            originLongitude: assetLoad?.originLongitude,
            originRegionId: assetLoad?.originRegionId,
            originDistrictId: assetLoad?.originDistrictId,
            destinationType: assetLoad?.destinationType,
            destinationJobId: assetLoad?.destinationJobId,
            destinationLofaId: assetLoad?.destinationLofaId,
            destinationYardId: assetLoad?.destinationYardId,
            destinationRegionId: assetLoad?.destinationRegionId,
            destinationDistrictId: assetLoad?.destinationDistrictId,
            destinationLatitude: assetLoad?.destinationLatitude,
            destinationLongitude: assetLoad?.destinationLongitude,
            trailerId: assetLoad?.trailerId,
            trailerTypeId: assetLoad?.trailerTypeId,
            truckNumber: assetLoad?.truckNumber,
            driverId: assetLoad?.driverId,
            miles: assetLoad?.miles,
            accessorialCost: assetLoad?.accessorialCost,
            payCode: assetLoad?.payCode,
            details: currentDetails,
        }

        const newDetails: AssetInfoTypeToSave[] = [];
        assetInfoToSave?.map(info => {
            if (info.assetTypeId)
                newDetails.push({
                    assetTypeId: info.assetTypeId,
                    assetType: info.assetType,
                    quantity: info.quantity,
                    assetId: info.assetId,
                    assetDescrip: info.assetDescrip,
                })
        })
        const newValues: AssetLoadType = {
            assetLoadTypeId: assetLoadToSave?.assetLoadTypeId,
            originType: assetLoadToSave?.originType,
            originJobId: assetLoadToSave?.originJobId,
            originLofaId: assetLoadToSave?.originLofaId,
            originYardId: assetLoadToSave?.originYardId,
            originLatitude: assetLoadToSave?.originLatitude,
            originLongitude: assetLoadToSave?.originLongitude,
            originRegionId: assetLoadToSave?.originRegionId,
            originDistrictId: assetLoadToSave?.originDistrictId,
            destinationType: assetLoadToSave?.destinationType,
            destinationJobId: assetLoadToSave?.destinationJobId,
            destinationLofaId: assetLoadToSave?.destinationLofaId,
            destinationYardId: assetLoadToSave?.destinationYardId,
            destinationRegionId: assetLoadToSave?.destinationRegionId,
            destinationDistrictId: assetLoadToSave?.destinationDistrictId,
            destinationLatitude: assetLoadToSave?.destinationLatitude,
            destinationLongitude: assetLoadToSave?.destinationLongitude,
            trailerId: assetLoadToSave?.trailerId,
            trailerTypeId: assetLoadToSave?.trailerTypeId,
            truckNumber: assetLoadToSave?.truckNumber,
            driverId: assetLoadToSave?.driverId,
            miles: assetLoadToSave?.miles,
            accessorialCost: assetLoadToSave?.accessorialCost,
            payCode: assetLoadToSave?.payCode,
            details: newDetails,
        }

        onClickSaveForEdit && onClickSaveForEdit(currentValues, newValues);
    }

    const handleOnClickSave = () => {
        // Validation - If Mob load then asset type must no be OOS
        setValidationError('');
        const outOfServiceEquipments: string[] = [];
        assetInfoToSave?.map(info => {
            const equimentType = info.assetType?.toLowerCase().replace('-', '').replace(' ', '');
            if (equimentType && equipmentsInfo)
                equipmentsInfo[equimentType]?.map(equipment => {
                    if (info.assetId === equipment.itemId && equipment.status === "Out of Service")
                        outOfServiceEquipments.push(equipment.descrip);
                })
        })
        if (outOfServiceEquipments.length > 0) {
            let errorMessage = 'Equipments - ';
            if (outOfServiceEquipments.length === 1) {
                errorMessage = `Equipment - ${outOfServiceEquipments[0]} is Out of Service`;
            } else {
                outOfServiceEquipments.map(outOfServiceEquipment => {
                    errorMessage += `${outOfServiceEquipment}, `
                })
                errorMessage += 'are Out of Service';
            }
            setValidationError(errorMessage);

            if (assetLoadToSave?.assetLoadType !== "Mobilization Load")
                setIsContinueMode(true);
            return;
        }
        else {
            if (loadType === "Edit")
                handleOnClickSaveForEdit();
            else
                handleOnClickSaveForAdd();
        }
    }

    return (
        <Modal show={showModal} onHide={onClickCancel} dialogClassName="AddAsset-Container">
            <Modal.Body className="AddAsset-Body">
                <div className="AddAsset-Header">
                    <div className='AddAsset-Header-Text'>
                        {
                            loadType === "Edit" ? `Edit Equipment Load - ${loadId} ` : "Add New Load"
                        }
                    </div>
                    <div className="AddAsset-HeaderButtonsContainer">
                        <div className='AddAsset-CancelButton'>
                            <CustomButton
                                title="Cancel"
                                isBordered
                                onClick={onClickCancel}
                            />
                        </div>
                        <CustomButton
                            title="Save"
                            onClick={handleOnClickSave}
                            isDisabled={isSaveButtonDisabled() || loading}
                        />
                    </div>
                </div>

                <div className="AddAsset-DividerLine" />

                {
                    (loading || assetLoadLoading || assetInfoLoading) && <div className="Equipments-Spinner">
                        <Loading />
                    </div>
                }
                {error && <div className="AddAsset-ErrorContainer"><Error error={error} /></div>}
                {assetInfoError && <div className="AddAsset-ErrorContainer"><Error error={assetInfoError} /></div>}
                {assetLoadError && <div className="AddAsset-ErrorContainer"><Error error={assetLoadError} /></div>}
                {!isContinueMode && <div className="AddAsset-ErrorContainer">{validationError}</div>}

                <div className="AddAsset-BodyContainer">
                    <AddEquipmentsDetails
                        assetInfo={assetInfo}
                        assetInfoToSave={assetInfoToSave}
                        setAssetInfoToSave={setAssetInfoToSave}
                        equipmentsInfo={equipmentsInfo}
                        updatePaycode={updatePaycode}
                        infoLoading={equipmentInfoDataLoading}
                        disableEquipmentIdSelection={isMultiLoadCreate}
                    />
                    <AddLoadDriverDetails
                        assetInfoExists={loadType === "Edit" ? assetInfoExists : false}
                        loadType={loadType}
                        assetLoadToSave={assetLoadToSave}
                        setAssetLoadToSave={setAssetLoadToSave}
                        payCodeDropdownOptions={payCodeList}
                        skipDropDowns={(!origin && !destination) ? false : (origin?.id !== -1 && destination?.id !== -1)}
                        lockDriverIdentityFields={isMultiLoadCreate}
                    />
                </div>

                {isContinueMode && (
                    <Modal show={isContinueMode} onHide={() => setIsContinueMode(false)} centered>
                        <Modal.Header className="AddAsset-ConfirmationModal-Head">
                            Out of Service
                            <i className="material-icons AddAsset-ConfirmationModal-X-Button" onClick={() => setIsContinueMode(false)}>
                                close
                            </i>
                        </Modal.Header>
                        <Modal.Body>
                            {validationError}. Do you want to continue?
                        </Modal.Body>
                        <Modal.Footer>
                            <CustomButton title="Cancel" onClick={() => setIsContinueMode(false)} isBordered />
                            <CustomButton title="Continue" onClick={loadType === "Edit" ? handleOnClickSaveForEdit : handleOnClickSaveForAdd} />
                        </Modal.Footer>
                    </Modal>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default AddAssetLoadModal;

